import React, { useEffect, useRef, useState } from "react";
import { CircularProgress, MenuItem, TextField } from "@mui/material";
import { useIntl } from "react-intl";
import insightsApi from "../../api/insightsApi";
import { getPillarLibrary } from "../../utils/pillarStorage";
import {
    emptyRow,
    hasDraft,
    loadPillarsProject,
    rowsFromProjectPillars,
    savePillarsProject,
} from "./pillarsDef";

// "Start from a project's pillars" — the top of the Generate Full Report
// modal's pillar editor.
//
// The modal lives on the insights list, where no project is in scope (a report
// joins a project as a wave only after it exists), so the project is picked
// here. Picking one replaces the rows with that project's declared pillar set —
// the six plus whatever it has added, e.g. inline from an impact — so a run
// starts from the vocabulary the project is already using. The rest of the
// modal's flow is untouched: the rows are still editable, still sent as
// `pillars_def`.
//
// The last pick is remembered per browser, and re-applied on the next open —
// unless an unfinished draft exists, which wins exactly as it already did over
// the library.
export default function ProjectPillarsPicker({ onLoad, disabled = false }) {
    const intl = useIntl();
    const [projects, setProjects] = useState([]);
    const [projectId, setProjectId] = useState(() => loadPillarsProject());
    const [loading, setLoading] = useState(false);
    // The latest request wins: switching projects twice quickly must not let
    // the first answer land last.
    const requestRef = useRef(0);

    const loadFrom = async (id) => {
        const request = ++requestRef.current;
        if (!id) {
            // An explicit "back to my defaults" — the library, not a draft.
            const library = getPillarLibrary();
            onLoad(library.length ? library.map(({ name, definition }) => ({ name, definition })) : [emptyRow()]);
            return;
        }
        setLoading(true);
        try {
            const pillars = await insightsApi.listProjectPillars(id);
            if (request === requestRef.current) onLoad(rowsFromProjectPillars(pillars));
        } catch (error) {
            console.error("Failed to load the project's pillars:", error);
        } finally {
            if (request === requestRef.current) setLoading(false);
        }
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const data = await insightsApi.getProjects({ page_size: 100 });
                if (cancelled) return;
                const list = Array.isArray(data) ? data : data?.results || [];
                setProjects(list);
                const remembered = loadPillarsProject();
                if (!remembered) return;
                if (!list.some((p) => String(p.id) === remembered)) {
                    // The project is gone (or no longer shared): forget it.
                    savePillarsProject("");
                    setProjectId("");
                    return;
                }
                if (!hasDraft()) loadFrom(remembered);
            } catch (error) {
                console.error("Failed to load projects for the pillar picker:", error);
            }
        })();
        return () => {
            cancelled = true;
        };
        // Once per mount — the modal mounts this each time it opens.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChange = (e) => {
        const id = String(e.target.value || "");
        setProjectId(id);
        savePillarsProject(id);
        loadFrom(id);
    };

    return (
        <TextField
            select
            fullWidth
            size="small"
            sx={{ mb: 2 }}
            disabled={disabled}
            label={intl.formatMessage({
                id: "insight.pillars_from_project",
                defaultMessage: "Start from a project's pillars",
            })}
            value={projects.some((p) => String(p.id) === projectId) ? projectId : ""}
            onChange={handleChange}
            InputProps={loading ? { endAdornment: <CircularProgress size={16} sx={{ mr: 3 }} /> } : undefined}
            helperText={intl.formatMessage({
                id: "insight.pillars_from_project_help",
                defaultMessage: "Replaces the rows below with that project's pillars. You can still edit them.",
            })}
        >
            <MenuItem value="">
                {intl.formatMessage({
                    id: "insight.pillars_from_library",
                    defaultMessage: "My default pillars",
                })}
            </MenuItem>
            {projects.map((project) => (
                <MenuItem key={project.id} value={String(project.id)}>
                    {project.name ||
                        intl.formatMessage(
                            { id: "insight.project_untitled", defaultMessage: "Project #{id}" },
                            { id: project.id }
                        )}
                </MenuItem>
            ))}
        </TextField>
    );
}
